"use client";

import { useAuthUserContext } from "@/contexts/authUserContext";
import { ErrorResponse, UserProfileResponse } from "@/lib/types";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import UserProfileActions from "./UserProfileActions";


type Submission = {
    title: string;
    media: { url: string; type: "image" | "video"; thumbnail?: string }[];
    bayan_video_url?: string;
    location_sub_district?: string | number;
    submitted_at: string;
};

function ActionButton({label, onClick}: {label: string, onClick: () => void}) {
    return <button className="bg-[#d10846] text-white p-2.5 rounded-lg w-full cursor-pointer" onClick={onClick}>{label}</button>
}

export default function UserDetails({userProfile}: {userProfile: UserProfileResponse | ErrorResponse}) {

    const [showUserProfileActions, setShowUserProfileActions] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const {user, isAuthenticated} = useAuthUserContext()
    const router = useRouter();

    const isPrivilegedUser = (userProfile && !('error' in userProfile) && userProfile.user_info?.is_ai_creator) || user?.email === 'harshul@inshorts.com';

    useEffect(() => {
        if (isPrivilegedUser) {
            try {
                const saved = localStorage.getItem("create_content_submissions");
                setSubmissions(saved ? JSON.parse(saved) : []);
            } catch {}
        }
    }, [isPrivilegedUser]);

    if ('error' in userProfile) {
        return <div className="text-red-500">Error loading user profile: {userProfile.message}</div>;
    }

    let action_button_node = <ActionButton label="Login" onClick={() => router.push('/auth/login')} />
    if(isAuthenticated && user) {
        if(user.uid === userProfile.user_info?.id) {
            if (isPrivilegedUser) {
                action_button_node = (
                <div className="flex gap-2 w-full">
                    <button className="bg-[#d10846] text-white p-2.5 rounded-lg flex-1 cursor-pointer" onClick={() => router.push('/feed/create-news')}>
                        Create Content
                    </button>
                    {submissions.length > 0 && (
                        <button className="bg-white text-[#d10846] border border-[#d10846] p-2.5 rounded-lg flex-1 cursor-pointer" onClick={() => setShowHistory(true)}>
                            Submitted Content
                        </button>
                    )}
                </div>
            )
            } else {
                action_button_node = <ActionButton label="Create Post" onClick={() => router.push('/feed/create')} />
            }
        } else {
            action_button_node = <ActionButton label="My Profile" onClick={() => router.push(`/user/profile/${user.uid}`)} />
        }
    }

    return (
        <div>
            <div className="flex">
                <div className="flex flex-1 flex-col items-center space-y-2">
                    <div>
                        <Image src={userProfile.user_info?.photo_url || '/default-profile.png'} alt="Profile Picture" width={96} height={96} className="w-24 h-24 rounded-full object-cover" />
                    </div>
                    <div className="flex">
                        <h1 className="capitalize font-bold text-base text-[#2a2a2a]">{userProfile.user_info?.name}</h1>
                        {userProfile.user_info?.id===user?.uid && <button className="cursor-pointer" onClick={() => setShowUserProfileActions(true)}>
                            <ChevronDownIcon className="size-6" />
                        </button>}
                    </div>
                    <div><span>@{userProfile.user_info?.user_name}</span></div>
                </div>
                <div className="flex flex-1 flex-col justify-between items-center space-y-2">
                    <div className="flex flex-1 justify-between w-full">
                        <div className="flex flex-1 flex-col items-center space-y-1">
                            <span className="font-bold">{userProfile.user_info?.users_followers_count || 0}</span>
                            <span>Followers</span>
                        </div>
                        <div className="flex flex-1 flex-col items-center space-y-1">
                            <span className="font-bold">{userProfile.user_info?.users_following_count || 0}</span>
                            <span>Following</span>
                        </div>
                    </div>
                    <div className="flex flex-1 items-center w-full">
                        {action_button_node}
                    </div>
                </div>
            </div>
                {showUserProfileActions && (
                    <UserProfileActions
                        open={showUserProfileActions}
                        onClose={() => setShowUserProfileActions(false)}
                        user_info={userProfile.user_info}
                    />
                )}
                {showHistory && (
                    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={() => setShowHistory(false)}>
                        <div className="bg-white w-full max-w-lg rounded-t-2xl max-h-[75vh] flex flex-col" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E0E0E0]">
                                <span className="font-semibold text-base">Submitted Content</span>
                                <button className="text-[#9B9B9B] text-xl cursor-pointer" onClick={() => setShowHistory(false)}>×</button>
                            </div>
                            <div className="overflow-y-auto flex-1 divide-y divide-[#E0E0E0]">
                                {submissions.map((s, i) => (
                                    <div key={i} className="flex gap-3 px-4 py-3 items-start">
                                        {s.media?.[0] && (
                                            <Image src={s.media[0].thumbnail || s.media[0].url} alt="" width={56} height={56} className="w-14 h-14 rounded object-cover flex-shrink-0" />
                                        )}
                                        <div className="flex flex-col flex-1 min-w-0">
                                            <span className="text-sm font-medium text-[#2a2a2a] line-clamp-2">{s.title}</span>
                                            <span className="text-xs text-[#9B9B9B] mt-1">{new Date(s.submitted_at).toLocaleString()}</span>
                                            <div className="flex gap-2 mt-1 text-xs text-[#9B9B9B]">
                                                {s.media.length > 0 && <span>{s.media.length} media</span>}
                                                {s.bayan_video_url && <span>· Byte/Bayan</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
}